import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'

import { PageHeader } from '@/components/shared'
import { Button } from '@/components/ui/button'
import { Check, X } from 'lucide-react'
import { authorService } from '@/services/authorService'
import { AuthorForm } from '@/components/AdminPanel/Author/AuthorForm'

const emptyDraft = {
  full_name: '',
  image: '',
  email: '',
  contact_number: '',
  role_id: null,
  description: '',
}

const AuthorEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = Boolean(id)

  const [draft, setDraft] = useState(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [initialized, setInitialized] = useState(!isEditing)

  const { data, isLoading } = useQuery(['author', id], () => authorService.getAuthorById(id), {
    enabled: isEditing,
    onError: (err) => toast.error('Failed to load author: ' + (err.response?.data?.error || err.message))
  })

  useEffect(() => {
    if (!isEditing || !data?.data?.author || initialized) return
    const author = data.data.author
    setDraft({
      full_name: author.full_name || '',
      image: author.image || '',
      email: author.email || '',
      contact_number: author.contact_number || '',
      role_id: author.role_id || null,
      description: author.description || '',
    })
    setInitialized(true)
  }, [data, isEditing, initialized])

  async function handleUpload(file) {
    setUploading(true)
    try {
      const response = await authorService.uploadImage(id || 'temp', file)
      return response.data?.url || ''
    } catch (err) {
      toast.error('Failed to upload image: ' + (err.response?.data?.error || err.message))
      return ''
    } finally {
      setUploading(false)
    }
  }

  async function handleSubmit() {
    if (!draft.full_name) {
      toast.error('Full name is required')
      return
    }

    setSaving(true)
    try {
      if (isEditing) {
        await authorService.updateAuthor(id, draft)
      } else {
        await authorService.createAuthor(draft)
      }
      toast.success('Author saved')
      queryClient.invalidateQueries('authors')
      if (isEditing) queryClient.invalidateQueries(['author', id])
      navigate('/admin-panel/authors')
    } catch (err) {
      toast.error('Failed to save author: ' + (err.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }

  const formId = 'author-editor-form'

  if (isEditing && (isLoading || !initialized)) {
    return <p className="text-sm text-muted-foreground">Loading author...</p>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? 'Edit author' : 'New author'}
        actions={
          <div className="flex items-center gap-3">
            <Button type="submit" form={formId} disabled={saving || uploading}>
              <Check className="mr-1.5 h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin-panel/authors')}>
              <X className="mr-1.5 h-4 w-4" /> Cancel
            </Button>
          </div>
        }
      />

      <AuthorForm
        formId={formId}
        draft={draft}
        setDraft={setDraft}
        uploading={uploading}
        onUpload={handleUpload}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

export default AuthorEditor
